/**
 * ViewerSettingsManager — Core Layer state container for PostFX + renderer settings.
 *
 * Pure state logic — no Three.js, no R3F, no DOM. The R3F `PostFxPipeline`
 * component reads state from here and applies it to the actual renderer / EffectComposer.
 *
 * Responsibilities:
 * - Resolve presets → full ViewerSettingsState
 * - Merge partial configs into current state
 * - Provide getState() for the PostFxPipeline component to read
 * - Track which preset is active (for UI display / serialisation)
 *
 * @see docs/ims-engine-api/02-api-contract.md — setViewerSettings
 * @see docs/ims-engine-api/01-architecture.md — Core Layer: PostProcessing
 */

import type {
  ViewerSettingsConfig,
  ViewerSettingsState,
  ViewerSettingsPresetId,
  ViewerSettingsPresetDefinition,
  BloomConfig,
  SsaoConfig,
  ToneMappingMode,
  AntialiasingMode,
} from '../api/types/viewerSettings';

// ---------------------------------------------------------------------------
// Built-in Presets
// ---------------------------------------------------------------------------

const PRESETS: Record<ViewerSettingsPresetId, ViewerSettingsPresetDefinition> = {
  cinematic: {
    bloom: { enabled: true, strength: 0.03, threshold: 1.5, radius: 0.25 },
    ssao: { enabled: true, intensity: 0.6, radius: 0.05, samples: 16 },
    antialiasing: 'smaa',
    sharpen: 0,
    toneMapping: 'aces-filmic',
    exposure: 1.0,
    dpr: 2, // always 2× — supersampling on 1x displays, native on Retina
    maxDpr: 2,
    paused: false,
  },
  balanced: {
    bloom: { enabled: true, strength: 0.03, threshold: 2.0, radius: 0.15 },
    ssao: { enabled: false, intensity: 1.0, radius: 0.05, samples: 16 },
    antialiasing: 'smaa',
    sharpen: 0,
    toneMapping: 'aces-filmic',
    exposure: 1.0,
    dpr: null,
    maxDpr: 1.5,
    paused: false,
  },
  performance: {
    bloom: { enabled: false, strength: 0, threshold: 1, radius: 0 },
    ssao: { enabled: false, intensity: 1.0, radius: 0.05, samples: 8 },
    antialiasing: 'off',
    sharpen: 0,
    toneMapping: 'aces-filmic',
    exposure: 1.0,
    dpr: 1.0,
    maxDpr: 1,
    paused: false,
  },
};

/** Default preset applied when no viewerSettings are provided. */
const DEFAULT_PRESET: ViewerSettingsPresetId = 'balanced';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve effective DPR from config dpr, maxDpr, and device native. */
function resolveEffectiveDpr(dpr: number | null, maxDpr: number): number {
  const native = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  const base = dpr ?? native;
  return Math.min(base, maxDpr);
}

/** Merge a partial BloomConfig into a full BloomConfig. */
function mergeBloom(
  current: Required<BloomConfig>,
  partial: Partial<BloomConfig>,
): Required<BloomConfig> {
  return {
    enabled: partial.enabled ?? current.enabled,
    strength: partial.strength ?? current.strength,
    threshold: partial.threshold ?? current.threshold,
    radius: partial.radius ?? current.radius,
  };
}

/** Merge a partial SsaoConfig into a full SsaoConfig. */
function mergeSsao(
  current: Required<SsaoConfig>,
  partial: Partial<SsaoConfig>,
): Required<SsaoConfig> {
  return {
    enabled: partial.enabled ?? current.enabled,
    intensity: partial.intensity ?? current.intensity,
    radius: partial.radius ?? current.radius,
    samples: partial.samples ?? current.samples,
  };
}

/** Check if a string is a known preset ID. */
export function isPresetId(value: string): value is ViewerSettingsPresetId {
  return value in PRESETS;
}

// ---------------------------------------------------------------------------
// ViewerSettingsManager
// ---------------------------------------------------------------------------

export class ViewerSettingsManager {
  private _state: ViewerSettingsState;
  /** Monotonic version counter — bumped on every state change. */
  private _version = 0;
  /** Subscribers notified on every state change. */
  private _listeners: Array<(state: ViewerSettingsState) => void> = [];

  constructor(initial?: ViewerSettingsPresetId | ViewerSettingsConfig) {
    // Start from balanced preset, then apply user's initial config on top.
    const base = PRESETS[DEFAULT_PRESET];
    this._state = this._resolvePreset(base, DEFAULT_PRESET);

    if (initial) {
      this.apply(initial);
    }
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /** Get a readonly snapshot of the current settings. */
  getState(): Readonly<ViewerSettingsState> {
    return this._state;
  }

  /** Monotonic version — lets React components know when to re-render. */
  get version(): number {
    return this._version;
  }

  /**
   * Apply a preset or merge partial config into the current state.
   *
   * - String → resolve preset, replace all fields.
   * - Object → merge into current state (shallow per-section merge).
   */
  apply(config: ViewerSettingsPresetId | ViewerSettingsConfig): void {
    if (typeof config === 'string') {
      if (!isPresetId(config)) {
        console.warn(`[ViewerSettingsManager] Unknown preset "${config}", ignoring.`);
        return;
      }
      const prev = this._fingerprint();
      const preset = PRESETS[config];
      this._state = this._resolvePreset(preset, config);
      if (this._fingerprint() === prev) return; // no change
    } else {
      const prev = this._fingerprint();
      this._mergeConfig(config);
      if (this._fingerprint() === prev) return; // no change
    }

    this._version++;
    this._notify();
  }

  /** Subscribe to state changes. Returns unsubscribe function. */
  subscribe(listener: (state: ViewerSettingsState) => void): () => void {
    this._listeners.push(listener);
    return () => {
      this._listeners = this._listeners.filter((l) => l !== listener);
    };
  }

  /** Cleanup. */
  dispose(): void {
    this._listeners = [];
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  private _resolvePreset(
    def: ViewerSettingsPresetDefinition,
    id: ViewerSettingsPresetId,
  ): ViewerSettingsState {
    return {
      bloom: { ...def.bloom },
      ssao: { ...def.ssao },
      antialiasing: def.antialiasing,
      sharpen: def.sharpen,
      toneMapping: def.toneMapping,
      exposure: def.exposure,
      dpr: resolveEffectiveDpr(def.dpr, def.maxDpr),
      maxDpr: def.maxDpr,
      paused: def.paused,
      activePreset: id,
    };
  }

  private _mergeConfig(config: ViewerSettingsConfig): void {
    // If a base preset is named, apply it first so we start from a clean slate.
    if (config.preset !== undefined && isPresetId(config.preset)) {
      this._state = this._resolvePreset(PRESETS[config.preset], config.preset);
    }

    const s = this._state;

    if (config.bloom !== undefined) {
      s.bloom = mergeBloom(s.bloom, config.bloom);
    }
    if (config.ssao !== undefined) {
      s.ssao = mergeSsao(s.ssao, config.ssao);
    }
    if (config.antialiasing !== undefined) {
      s.antialiasing = config.antialiasing;
    }
    if (config.sharpen !== undefined) {
      s.sharpen = Math.max(0, Math.min(1, config.sharpen));
    }
    if (config.toneMapping !== undefined) {
      s.toneMapping = config.toneMapping;
    }
    if (config.exposure !== undefined) {
      s.exposure = config.exposure;
    }
    if (config.maxDpr !== undefined) {
      s.maxDpr = config.maxDpr;
    }
    if (config.dpr !== undefined) {
      s.dpr = resolveEffectiveDpr(config.dpr, s.maxDpr);
    }
    if (config.paused !== undefined) {
      s.paused = config.paused;
    }

    // Once a manual override is applied, clear the preset tag.
    s.activePreset = null;
  }

  private _notify(): void {
    for (const listener of this._listeners) {
      listener(this._state);
    }
  }

  /** Cheap scalar fingerprint for change detection — avoids notifying on no-op applies. */
  private _fingerprint(): string {
    const s = this._state;
    return `${s.bloom.enabled}|${s.bloom.strength}|${s.bloom.threshold}|${s.bloom.radius}|${s.ssao.enabled}|${s.ssao.intensity}|${s.ssao.radius}|${s.ssao.samples}|${s.antialiasing}|${s.sharpen}|${s.toneMapping}|${s.exposure}|${s.dpr}|${s.maxDpr}|${s.paused}|${s.activePreset}`;
  }
}

// ---------------------------------------------------------------------------
// Exports for testing / internal use
// ---------------------------------------------------------------------------

export { PRESETS as viewerSettingsPresets };
export type { ToneMappingMode, AntialiasingMode };
